export function useFacilityRoute() {
  const setupFlowRouteName = "setup-facilityId";
  const rootImportRouteName = `${setupFlowRouteName}-import`;
  const bulkImportRoutePrefix = `${rootImportRouteName}-`;
  const INTRO_STEP_INDEX = 0;
  const INFO_STEP_INDEX = 1;

  const {
    rootRoute,
    currentRoute,
    parseRoute,
    makeGuardResult,
    resolveRouteContext,
  } = useRouteGuard();

  const currentRouteContext = computed(() =>
    resolveFacilityRouteContext(currentRoute.value),
  );

  const resolveFacilityRouteContext = (route: RouteContextInput) => {
    const result = resolveRouteContext(route, {
      matchPrefixes: {
        setupFlow: setupFlowRouteName,
        importFlow: rootImportRouteName,
        bulkImportFlow: bulkImportRoutePrefix,
        taskListFlow: `${bulkImportRoutePrefix}task-list`,
        uploadFile: `${bulkImportRoutePrefix}pageId-import-into-table`,
        mapColumns: `${bulkImportRoutePrefix}pageId-map-columns`,
        mergeConflicts: `${bulkImportRoutePrefix}pageId-merge-conflicts`,
        learnMore: `${bulkImportRoutePrefix}pageId-learn-more`,
        createFacilityIntroStep: `${setupFlowRouteName}-intro`,
        createFacilityInfoStep: `${setupFlowRouteName}-info`,
      },
      paramsDefaults: {
        facilityId: (id: string | undefined) => (id ? Number(id) : undefined),
        pageId: undefined as string | undefined,
      },
      defaults: {
        invalidFacilityId: false,
        isFacilityCreationStep: false,
        isFacilityWizard: false,
        facilityWizardStepIndex: undefined as number | undefined,
      },
    });

    if (!result.normalizedName || !result.matches.setupFlow) {
      return result;
    }

    result.invalidFacilityId =
      result.params.facilityId !== undefined
        ? isNaN(result.params.facilityId)
        : false;

    result.params.pageId = result.matches.taskListFlow
      ? "task-list"
      : result.params.pageId;

    result.isFacilityCreationStep =
      !result.params.facilityId &&
      (!!result.matches.createFacilityIntroStep ||
        !!result.matches.createFacilityInfoStep);

    result.isFacilityWizard =
      !result.matches.importFlow &&
      (!!result.isFacilityCreationStep || !!result.matches.setupFlow);

    const facilityWizardStep = result.isFacilityWizard
      ? result.normalizedName.substring(setupFlowRouteName.length + 1)
      : undefined;

    result.facilityWizardStepIndex = result.isFacilityWizard
      ? Math.max(
          0,
          facilitySteps.findIndex((step) =>
            facilityWizardStep?.endsWith(step.url),
          ),
        )
      : undefined;

    return result;
  };

  const createFacilityPath = (
    options:
      | {
          error: true;
          facilityId?: number | "current";
        }
      | {
          wizardStep: number | FacilityStepId | FacilityStep | "blank";
          facilityId?: number | "current";
          omitValidation?: boolean;
        }
      | {
          facilityId: number | "current";
          import: true;
          pageId?: string | "current";
        }
      | {
          facilityId: number | "current";
          import: true;
          pageId: string | "current";
          subPage: string;
        },
  ) => {
    const facilityId =
      options.facilityId === "current"
        ? currentRoute.value.params.facilityId?.toString()
        : options.facilityId?.toString();

    if ("error" in options) {
      // TODO: handle error case properly
      return parseRoute("/error");
    }

    if ("wizardStep" in options) {
      if (options.wizardStep === "blank") {
        return parseRoute({
          name: setupFlowRouteName,
          params: { facilityId },
        });
      }
      const facilityStep =
        typeof options.wizardStep === "number"
          ? facilitySteps[options.wizardStep]
          : typeof options.wizardStep === "string"
            ? facilitySteps.find((step) => step.id === options.wizardStep)
            : options.wizardStep;

      const isValidStep =
        facilityStep !== undefined &&
        (options.omitValidation ||
          (options.facilityId
            ? facilityStep.index >= INFO_STEP_INDEX // facilityId allowed only for steps other than intro
            : facilityStep.index <= INFO_STEP_INDEX)); // steps above intro are not allowed without facilityId

      if (!isValidStep) {
        return parseRoute({
          name: setupFlowRouteName,
          params: { facilityId },
        });
      }

      return parseRoute({
        name: `${setupFlowRouteName}-${facilityStep.url}`,
        params: { facilityId },
      });
    }

    if ("import" in options && !options.import) {
      devConsole.warn(
        "cksetup.wizard.createFacilityPath called without import flag",
      );
      return parseRoute({
        name: setupFlowRouteName,
        params: {
          facilityId,
        },
      });
    }

    const pageId =
      options.pageId === "current"
        ? resolveFacilityRouteContext(currentRoute.value).params.pageId
        : options.pageId?.toString();

    if ("subPage" in options && options.pageId) {
      return parseRoute({
        name: `${bulkImportRoutePrefix}pageId-${options.subPage}`,
        params: {
          facilityId,
          pageId,
        },
      });
    }

    if (options.pageId) {
      return parseRoute({
        name: `${bulkImportRoutePrefix}pageId`,
        params: {
          facilityId,
          pageId,
        },
      });
    }

    return parseRoute({
      name: rootImportRouteName,
      params: {
        facilityId,
        pageId,
      },
    });
  };

  return {
    parseRoute,
    makeGuardResult,
    resolveFacilityRouteContext,
    createFacilityPath,
    rootRoute,
    currentRouteContext,
    INTRO_STEP_INDEX,
    INFO_STEP_INDEX,
  };
}
